import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [mounted, setMounted] = useState(false);
  const [theme, setThemeState] = useState(() => {
    // Initialize from localStorage or default to system
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("theme");
      if (stored && ["light", "dark", "system"].includes(stored)) {
        return stored;
      }
    }
    return "system";
  });

  // Resolve the actual theme (light/dark) from system preference
  const [resolvedTheme, setResolvedTheme] = useState("light");

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    // Function to update the resolved theme
    const updateResolvedTheme = (currentTheme) => {
      let resolved;

      if (currentTheme === "system") {
        resolved = window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light";
      } else {
        resolved = currentTheme;
      }

      setResolvedTheme(resolved);

      // Apply theme to document
      if (resolved === "dark") {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    };

    // Initial theme application
    updateResolvedTheme(theme);

    // Listen for system theme changes when theme is set to "system"
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = () => {
      if (theme === "system") {
        updateResolvedTheme("system");
      }
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [theme]);

  const setTheme = (newTheme) => {
    if (["light", "dark", "system"].includes(newTheme)) {
      setThemeState(newTheme);
      localStorage.setItem("theme", newTheme);
    }
  };

  const value = {
    theme,
    resolvedTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {mounted && children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}

export default ThemeProvider;
